import React, { useCallback, useState } from "react";
import { Row, Label, FormGroup, Button, Col } from "reactstrap";
//Import Flatepicker
import "flatpickr/dist/themes/material_blue.css";
import { useDropzone } from "react-dropzone";
import { savePDF, uploadPDF } from "../../../helpers/AuthType/authBackend";
import { splitFileIntoChunksBase64 } from "shared/fileUtils";
import { BiLoaderAlt, BiCheckCircle } from "react-icons/bi";

const MAX_PDF_SIZE = 20 * 1024 * 1024; // 20MB

interface IdentityInformationProps {
  onSubmit: (values: any) => void;
  onBack: () => void;
  data?: any;
  userId: any;
}

const LegalDocumentation = ({
  onSubmit,
  onBack,
  data,
  userId,
}: IdentityInformationProps) => {
  //meta title
  document.title = "Ditobanx";

  const [selectedConstitutionLetter, setSelectedConstitutionLetter] = useState<
    any[]
  >([]);
  const [
    selectedLegalRepresentationCertificate,
    setSelectedLegalRepresentationCertificate,
  ] = useState<any[]>([]);
  const [selectedRegistrationCertificate, setSelectedRegistrationCertificate] =
    useState<any[]>([]);
  const [selectedLicenses, setSelectedLicenses] = useState<any[]>([]);
  const [selectedNIT, setSelectedNIT] = useState<any[]>([]);
  const [validateConstitution, setValidateConstitution] =
    useState<boolean>(false);
  const [validateCertificate, setValidateCerticate] = useState<boolean>(false);
  const [validateRegistrationCertificate, setValidateRegistrationCertificate] =
    useState<boolean>(false);
  const [validateNIT, setValidateNIT] = useState<boolean>(false);
  const [validateLicenses, setValidateLicenses] = useState<boolean>(false);
  //---------------------------------------------
  const [loadingCL, setLoadingCL] = useState(false); // Carta Constitución
  const [loadingLR, setLoadingLR] = useState(false); // Legal Rep. Cert.
  const [loadingRC, setLoadingRC] = useState(false); // Registro
  const [loadingNI, setLoadingNI] = useState(false); // NIT
  const [loadingLA, setLoadingLA] = useState(false); // Licencias
  //---------------------------------------------
  // Función para el documento de Constitución (solo PDF, sin validar tamaño)
  const onDropConstitutionLetter = useCallback((acceptedFiles: any[]) => {
    // Filtrar únicamente por tipo MIME PDF
    const validFiles = acceptedFiles.filter((file) => {
      if (file.type !== "application/pdf") {
        alert(`El archivo ${file.name} no es un PDF válido.`);
        return false;
      }
      if (file.size > MAX_PDF_SIZE) {
        alert(`El archivo ${file.name} es muy pesado.`);
        return false;
      }
      return true;
    });

    // Crear preview (blob URL) para cada PDF
    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    // Guardar en el estado (sin eliminar los anteriores)
    setSelectedConstitutionLetter((prevFiles) => [
      ...prevFiles,
      ...mappedFiles,
    ]);
  }, []);

  // Configuración de Dropzone para documentos de Constitución (solo PDF)
  const {
    getInputProps: getDocConstitutionInputProps,
    open: openDocConstitution,
  } = useDropzone({
    onDrop: onDropConstitutionLetter,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/pdf": [],
    },
    maxFiles: 1,
  });

  // Función para eliminar un documento por índice
  const removeDocConstitution = (idx: number) => {
    setSelectedConstitutionLetter((p) => p.filter((_, i) => i !== idx));
    setValidateConstitution(false);
    setLoadingCL(false);
  };

  //---------------------------------------------
  //---------------------------------------------
  //---------------------------------------------
  // Función para el certificado de representante legal (solo PDF, sin validar tamaño)
  const onDropRepresentativeCertificate = useCallback(
    (acceptedFiles: any[]) => {
      // Filtrar únicamente por tipo MIME PDF
      const validFiles = acceptedFiles.filter((file) => {
        if (file.type !== "application/pdf") {
          alert(`El archivo ${file.name} no es un PDF válido.`);
          return false;
        }
        if (file.size > MAX_PDF_SIZE) {
          alert(`El archivo ${file.name} es muy pesado.`);
          return false;
        }
        return true;
      });

      // Crear preview (blob URL) para cada PDF
      const mappedFiles = validFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      // Guardar en el estado (sin eliminar los anteriores)
      setSelectedLegalRepresentationCertificate((prevFiles) => [
        ...prevFiles,
        ...mappedFiles,
      ]);
    },
    []
  );

  // Dropzone para certificados de representante legal (solo PDF)
  const {
    getInputProps: getDocRepresentativeInputProps,
    open: openDocRepresentative,
  } = useDropzone({
    onDrop: onDropRepresentativeCertificate,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/pdf": [],
    },
    maxFiles: 1,
  });

  // Función para eliminar un certificado por índice
  const removeDocRepresentative = (idx: number) => {
    setSelectedLegalRepresentationCertificate((p) =>
      p.filter((_, i) => i !== idx)
    );
    setValidateCerticate(false); // o setValidateCertificate(false)
    setLoadingLR(false);
  };

  //---------------------------------------------
  //---------------------------------------------
  // Función para el documento de Contitutcion
  //---------------------------------------------
  // Función para el certificado de registro (solo PDF, sin validar tamaño)
  const onDropRegistrationCertificate = useCallback((acceptedFiles: any[]) => {
    // Filtrar únicamente por tipo MIME PDF
    const validFiles = acceptedFiles.filter((file) => {
      console.log(file.size > MAX_PDF_SIZE);
      if (file.type !== "application/pdf") {
        alert(`El archivo ${file.name} no es un PDF válido.`);
        return false;
      }
      if (file.size > MAX_PDF_SIZE) {
        alert(`El archivo ${file.name} es muy pesado.`);
        return false;
      }
      return true;
    });

    // Crear preview (blob URL) para cada PDF
    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    // Guardar en el estado (sin eliminar los anteriores)
    setSelectedRegistrationCertificate((prevFiles) => [
      ...prevFiles,
      ...mappedFiles,
    ]);
  }, []);

  // Dropzone para certificados de registro (solo PDF)
  const {
    getInputProps: getDocRegistrationInputProps,
    open: openDocRegistration,
  } = useDropzone({
    onDrop: onDropRegistrationCertificate,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/pdf": [],
    },
    maxFiles: 1,
  });

  // Función para eliminar un certificado por índice
  const removeDocRegistration = (idx: number) => {
    setSelectedRegistrationCertificate((p) => p.filter((_, i) => i !== idx));
    setValidateRegistrationCertificate(false);
    setLoadingRC(false);
  };

  //---------------------------------------------
  //---------------------------------------------
  // NIT
  const onDropNIT = useCallback((acceptedFiles: any[]) => {
    // Filtrar únicamente por tipo MIME PDF
    const validFiles = acceptedFiles.filter((file) => {
      if (file.type !== "application/pdf") {
        alert(`El archivo ${file.name} no es un PDF válido.`);
        return false;
      }
      return true;
    });

    // Crear preview (blob URL) para cada PDF
    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    // Guardar en el estado (sin eliminar los anteriores)
    setSelectedNIT((prevFiles) => [...prevFiles, ...mappedFiles]);
  }, []);

  // Dropzone para licencias (solo PDF)
  const { getInputProps: getDocNITInputProps, open: openDocNIT } = useDropzone({
    onDrop: onDropNIT,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/pdf": [],
    },
    maxFiles: 1,
  });

  // Función para eliminar una licencia por índice
  const removeDocNIT = (idx: number) => {
    setSelectedNIT((p) => p.filter((_, i) => i !== idx));
    setValidateNIT(false);
    setLoadingNI(false);
  };

  //---------------------------------------------
  //---------------------------------------------
  //---------------------------------------------
  // Función para licencias (solo PDF, sin validar tamaño)
  const onDropLicenses = useCallback((acceptedFiles: any[]) => {
    // Filtrar únicamente por tipo MIME PDF
    const validFiles = acceptedFiles.filter((file) => {
      if (file.type !== "application/pdf") {
        alert(`El archivo ${file.name} no es un PDF válido.`);
        return false;
      }
      if (file.size > MAX_PDF_SIZE) {
        alert(`El archivo ${file.name} es muy pesado.`);
        return false;
      }
      return true;
    });

    // Crear preview (blob URL) para cada PDF
    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    // Guardar en el estado (sin eliminar los anteriores)
    setSelectedLicenses((prevFiles) => [...prevFiles, ...mappedFiles]);
  }, []);

  // Dropzone para licencias (solo PDF)
  const { getInputProps: getDocLicensesInputProps, open: openDoclicenses } =
    useDropzone({
      onDrop: onDropLicenses,
      noClick: true,
      noKeyboard: true,
      accept: {
        "application/pdf": [],
      },
      maxFiles: 1,
    });

  // Función para eliminar una licencia por índice
  const removeDocRLicenses = (idx: number) => {
    setSelectedLicenses((p) => p.filter((_, i) => i !== idx));
    setValidateLicenses(false);
    setLoadingLA(false);
  };

  // ------------------------------------------

  const uploadPdfChunks = async (file: File, type: any) => {
    const chunks = await splitFileIntoChunksBase64(file);
    const uploadId = `${file.name}-${Date.now()}`;
    const totalChunks = chunks.length;

    for (let i = 0; i < totalChunks; i++) {
      await uploadPDF({
        uploadId,
        filename: file.name,
        chunkIndex: i,
        totalChunks,
        chunkBase64: chunks[i],
        type,
      });
    }

    // Finalizar ensamblaje
    const response = await savePDF({
      uploadId,
      filename: file.name,
      type,
      userId,
    });

    return response.data.filename; // nombre del PDF guardado
  };

  const validarPDFUploaded = async (type: string) => {
    try {
      if (type === "CL") {
        setLoadingCL(true);
        await uploadPdfChunks(selectedConstitutionLetter[0], type);
        setValidateConstitution(true);
      }
      if (type === "LR") {
        setLoadingLR(true);
        await uploadPdfChunks(selectedLegalRepresentationCertificate[0], type);
        setValidateCerticate(true); // (corrige nombre si es validateCertificate)
      }
      if (type === "RC") {
        setLoadingRC(true);
        await uploadPdfChunks(selectedRegistrationCertificate[0], type);
        setValidateRegistrationCertificate(true);
      }
      if (type === "LA") {
        setLoadingLA(true);
        await uploadPdfChunks(selectedLicenses[0], type);
        setValidateLicenses(true);
      }
      if (type === "NI") {
        setLoadingNI(true);
        await uploadPdfChunks(selectedNIT[0], type);
        setValidateNIT(true);
      }
    } catch (err) {
      console.error(err);
      alert("Ocurrió un error al subir el documento.");
    } finally {
      if (type === "CL") setLoadingCL(false);
      if (type === "LR") setLoadingLR(false);
      if (type === "RC") setLoadingRC(false);
      if (type === "LA") setLoadingLA(false);
      if (type === "NI") setLoadingNI(false);
    }
  };

  const handleOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      validateCertificate &&
      validateConstitution &&
      validateRegistrationCertificate &&
      validateNIT
    ) {
      onSubmit(true);
    } else {
      alert("Debes validar todos los documentos cargados antes de continuar");
    }
  };

  return (
    <React.Fragment>
      {/* <Form onSubmit={handleOnSubmit} autoComplete="off"> */}
      <Row>
        {/* Escritura de constitución */}
        <FormGroup className="mb-3">
          <Label htmlFor="documento-input">Escritura de constitución</Label>
          <p className="text-muted">
            Debes validar el documento una vez subido
          </p>

          <div
            className="d-flex flex-column align-items-center p-4 border rounded bg-light"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              openDocConstitution();
            }}
          >
            <i
              className="bx bx-file-pdf"
              style={{ fontSize: "48px", color: "#d9534f" }}
            />
            <h5 className="mt-2 mb-3 text-secondary">
              Selecciona un archivo PDF
            </h5>

            {/* Input oculto controlado por Dropzone */}
            <input
              {...getDocConstitutionInputProps()}
              style={{ display: "none" }}
            />

            <Button color="primary" size="sm">
              <i className="bx bx-upload mr-1" /> Subir Archivo
            </Button>

            {/* Previsualización para documento dentro del mismo contenedor */}
            {selectedConstitutionLetter.length > 0 && (
              <div className="mt-3 w-100 d-flex flex-wrap justify-content-center">
                {selectedConstitutionLetter.map((file: any, index: number) => (
                  <div
                    key={index}
                    className="position-relative m-2 p-3 border rounded bg-white"
                    style={{ width: "180px" }}
                  >
                    {/* Botón para eliminar */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDocConstitution(index);
                      }}
                      className="position-absolute btn btn-sm btn-danger rounded-circle"
                      style={{
                        top: "-10px",
                        right: "-10px",
                        width: "24px",
                        height: "24px",
                        padding: 0,
                        fontSize: "14px",
                        lineHeight: "1",
                      }}
                    >
                      &times;
                    </button>

                    {/* Ícono o miniatura PDF */}
                    <div className="d-flex justify-content-center mb-2">
                      <i
                        className="bx bx-file-pdf"
                        style={{ fontSize: "36px", color: "#d9534f" }}
                      />
                    </div>

                    {/* Nombre del archivo con enlace para abrir en nueva pestaña */}
                    <a
                      href={file.preview}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-block text-truncate text-center"
                      style={{ maxWidth: "100%" }}
                      title={file.name}
                    >
                      {file.name}
                    </a>
                  </div>
                ))}
              </div>
            )}
            {/* Carta Constitución (CL) */}
            {selectedConstitutionLetter.length > 0 && (
              <Button
                type="button"
                color={validateConstitution ? "success" : "primary"}
                disabled={validateConstitution || loadingCL}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  validarPDFUploaded("CL");
                }}
                className="mt-3 flex items-center gap-2"
              >
                {loadingCL && <BiLoaderAlt className="animate-spin" />}
                {validateConstitution && !loadingCL && (
                  <BiCheckCircle className="text-green-500" />
                )}
                {loadingCL
                  ? "Cargando…"
                  : validateConstitution
                  ? "Validado"
                  : "Verificar"}
              </Button>
            )}
          </div>
        </FormGroup>
        <Col lg={6} md={6} sm={12}>
          {/* Certificación de representación legal (Opcional) */}
          <FormGroup className="mb-3">
            <Label htmlFor="documento-input">
              Certificación de representación legal (Opcional)
            </Label>
            <p className="text-muted">
              Debes validar el documento una vez subido
            </p>

            <div
              className="d-flex flex-column align-items-center p-4 border rounded bg-light"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                openDocRepresentative();
              }}
            >
              <i
                className="bx bx-file-pdf"
                style={{ fontSize: "48px", color: "#d9534f" }}
              />
              <h5 className="mt-2 mb-3 text-secondary">
                Selecciona un archivo PDF
              </h5>

              {/* Input oculto controlado por Dropzone */}
              <input
                {...getDocRepresentativeInputProps()}
                style={{ display: "none" }}
              />

              <Button color="primary" size="sm">
                <i className="bx bx-upload mr-1" /> Subir Archivo
              </Button>

              {/* Previsualización para documento dentro del mismo contenedor */}
              {selectedLegalRepresentationCertificate.length > 0 && (
                <div className="mt-3 w-100 d-flex flex-wrap justify-content-center">
                  {selectedLegalRepresentationCertificate.map(
                    (file: any, index: number) => (
                      <div
                        key={index}
                        className="position-relative m-2 p-3 border rounded bg-white"
                        style={{ width: "180px" }}
                      >
                        {/* Botón para eliminar */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeDocRepresentative(index);
                          }}
                          className="position-absolute btn btn-sm btn-danger rounded-circle"
                          style={{
                            top: "-10px",
                            right: "-10px",
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            fontSize: "14px",
                            lineHeight: "1",
                          }}
                        >
                          &times;
                        </button>

                        {/* Ícono PDF */}
                        <div className="d-flex justify-content-center mb-2">
                          <i
                            className="bx bx-file-pdf"
                            style={{ fontSize: "36px", color: "#d9534f" }}
                          />
                        </div>

                        {/* Nombre del archivo con enlace para abrir en nueva pestaña */}
                        <a
                          href={file.preview}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block text-truncate text-center"
                          style={{ maxWidth: "100%" }}
                          title={file.name}
                        >
                          {file.name}
                        </a>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* Legal Representation Cert. (LR) */}
              {selectedLegalRepresentationCertificate.length > 0 && (
                <Button
                  type="button"
                  color={validateCertificate ? "success" : "primary"}
                  disabled={validateCertificate || loadingLR}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    validarPDFUploaded("LR");
                  }}
                  className="mt-3 flex items-center gap-2"
                >
                  {loadingLR && <BiLoaderAlt className="animate-spin" />}
                  {validateCertificate && !loadingLR && (
                    <BiCheckCircle className="text-green-500" />
                  )}
                  {loadingLR
                    ? "Cargando…"
                    : validateCertificate
                    ? "Validado"
                    : "Verificar"}
                </Button>
              )}
            </div>
          </FormGroup>
        </Col>
        <Col lg={6} md={6} sm={12}>
          {/* Certificado de registro */}
          <FormGroup className="mb-3">
            <Label htmlFor="documento-input">Certificado de registro</Label>
            <p className="text-muted">
              Debes validar el documento una vez subido
            </p>

            <div
              className="d-flex flex-column align-items-center p-4 border rounded bg-light"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                openDocRegistration();
              }}
            >
              <i
                className="bx bx-file-pdf"
                style={{ fontSize: "48px", color: "#d9534f" }}
              />
              <h5 className="mt-2 mb-3 text-secondary">
                Selecciona un archivo PDF
              </h5>

              {/* Input oculto controlado por Dropzone */}
              <input
                {...getDocRegistrationInputProps()}
                style={{ display: "none" }}
              />

              <Button color="primary" size="sm">
                <i className="bx bx-upload mr-1" /> Subir Archivo
              </Button>

              {/* Previsualización para documento dentro del mismo contenedor */}
              {selectedRegistrationCertificate.length > 0 && (
                <div className="mt-3 w-100 d-flex flex-wrap justify-content-center">
                  {selectedRegistrationCertificate.map(
                    (file: any, index: number) => (
                      <div
                        key={index}
                        className="position-relative m-2 p-3 border rounded bg-white"
                        style={{ width: "180px" }}
                      >
                        {/* Botón para eliminar */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeDocRegistration(index);
                          }}
                          className="position-absolute btn btn-sm btn-danger rounded-circle"
                          style={{
                            top: "-10px",
                            right: "-10px",
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            fontSize: "14px",
                            lineHeight: "1",
                          }}
                        >
                          &times;
                        </button>

                        {/* Ícono PDF */}
                        <div className="d-flex justify-content-center mb-2">
                          <i
                            className="bx bx-file-pdf"
                            style={{ fontSize: "36px", color: "#d9534f" }}
                          />
                        </div>

                        {/* Nombre del archivo con enlace para abrir en nueva pestaña */}
                        <a
                          href={file.preview}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block text-truncate text-center"
                          style={{ maxWidth: "100%" }}
                          title={file.name}
                        >
                          {file.name}
                        </a>
                      </div>
                    )
                  )}
                </div>
              )}
              {/* Registro (RC) */}
              {selectedRegistrationCertificate.length > 0 && (
                <Button
                  type="button"
                  color={
                    validateRegistrationCertificate ? "success" : "primary"
                  }
                  disabled={validateRegistrationCertificate || loadingRC}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    validarPDFUploaded("RC");
                  }}
                  className="mt-3 flex items-center gap-2"
                >
                  {loadingRC && <BiLoaderAlt className="animate-spin" />}
                  {validateRegistrationCertificate && !loadingRC && (
                    <BiCheckCircle className="text-green-500" />
                  )}
                  {loadingRC
                    ? "Cargando…"
                    : validateRegistrationCertificate
                    ? "Validado"
                    : "Verificar"}
                </Button>
              )}
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col lg={6} md={6} sm={12}>
          {/* NIT de la empresa */}
          <FormGroup className="mb-3">
            <Label htmlFor="documento-input">NIT de la empresa</Label>
            <p className="text-muted">
              Debes validar el documento una vez subido
            </p>

            <div
              className="d-flex flex-column align-items-center p-4 border rounded bg-light"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.preventDefault();
                openDocNIT();
              }}
            >
              <i
                className="bx bx-file-pdf"
                style={{ fontSize: "48px", color: "#d9534f" }}
              />
              <h5 className="mt-2 mb-3 text-secondary">
                Selecciona un archivo PDF
              </h5>

              {/* Input oculto controlado por Dropzone */}
              <input {...getDocNITInputProps()} style={{ display: "none" }} />

              <Button color="primary" size="sm">
                <i className="bx bx-upload mr-1" /> Subir Archivo
              </Button>

              {/* Previsualización para documentos dentro del mismo contenedor */}
              {selectedNIT.length > 0 && (
                <div className="mt-3 w-100 d-flex flex-wrap justify-content-center">
                  {selectedNIT.map((file: any, index: number) => (
                    <div
                      key={index}
                      className="position-relative m-2 p-3 border rounded bg-white"
                      style={{ width: "180px" }}
                    >
                      {/* Botón para eliminar */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeDocNIT(index);
                        }}
                        className="position-absolute btn btn-sm btn-danger rounded-circle"
                        style={{
                          top: "-10px",
                          right: "-10px",
                          width: "24px",
                          height: "24px",
                          padding: 0,
                          fontSize: "14px",
                          lineHeight: "1",
                        }}
                      >
                        &times;
                      </button>

                      {/* Ícono PDF */}
                      <div className="d-flex justify-content-center mb-2">
                        <i
                          className="bx bx-file-pdf"
                          style={{ fontSize: "36px", color: "#d9534f" }}
                        />
                      </div>

                      {/* Nombre del archivo con enlace para abrir en nueva pestaña */}
                      <a
                        href={file.preview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-block text-truncate text-center"
                        style={{ maxWidth: "100%" }}
                        title={file.name}
                      >
                        {file.name}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {/* NIT (NI) */}
              {selectedNIT.length > 0 && (
                <Button
                  type="button"
                  color={validateNIT ? "success" : "primary"}
                  disabled={validateNIT || loadingNI}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    validarPDFUploaded("NI");
                  }}
                  className="mt-3 flex items-center gap-2"
                >
                  {loadingNI && <BiLoaderAlt className="animate-spin" />}
                  {validateNIT && !loadingNI && (
                    <BiCheckCircle className="text-green-500" />
                  )}
                  {loadingNI
                    ? "Cargando…"
                    : validateNIT
                    ? "Validado"
                    : "Verificar"}
                </Button>
              )}
            </div>
          </FormGroup>
        </Col>
        <Col lg={6} md={6} sm={12}>
          {/* Licencias o permisos (si aplica) */}
          <FormGroup
            className="mb-3"
            style={{
              // backgroundColor: "#f7f7f7",
              padding: "10px",
              borderRadius: "10px",
            }}
          >
            <Label htmlFor="documento-input">
              Licencias o permisos (si aplica)
            </Label>
            <p className="text-muted">
              Opcional: Sube licencias operativas o permisos específicos para tu
              industria
              <br />
              Debes validar el documento una vez subido
            </p>

            <div className="d-flex flex-column align-items-center p-4 border rounded bg-light">
              <i
                className="bx bx-file-pdf"
                style={{ fontSize: "48px", color: "#d9534f" }}
              />
              <h5 className="mt-2 mb-3 text-secondary">
                Selecciona un archivo PDF
              </h5>

              {/* Input oculto controlado por Dropzone */}
              <input
                {...getDocLicensesInputProps()}
                style={{ display: "none" }}
              />

              <Button
                color="primary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  openDoclicenses();
                }}
              >
                <i className="bx bx-upload mr-1" /> Subir Archivo
              </Button>

              {/* Previsualización para documentos dentro del mismo contenedor */}
              {selectedLicenses.length > 0 && (
                <div className="mt-3 w-100 d-flex flex-wrap justify-content-center">
                  {selectedLicenses.map((file: any, index: number) => (
                    <div
                      key={index}
                      className="position-relative m-2 p-3 border rounded bg-white"
                      style={{ width: "180px" }}
                    >
                      {/* Botón para eliminar */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeDocRLicenses(index);
                        }}
                        className="position-absolute btn btn-sm btn-danger rounded-circle"
                        style={{
                          top: "-10px",
                          right: "-10px",
                          width: "24px",
                          height: "24px",
                          padding: 0,
                          fontSize: "14px",
                          lineHeight: "1",
                        }}
                      >
                        &times;
                      </button>

                      {/* Ícono PDF */}
                      <div className="d-flex justify-content-center mb-2">
                        <i
                          className="bx bx-file-pdf"
                          style={{ fontSize: "36px", color: "#d9534f" }}
                        />
                      </div>

                      {/* Nombre del archivo con enlace para abrir en nueva pestaña */}
                      <a
                        href={file.preview}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-block text-truncate text-center"
                        style={{ maxWidth: "100%" }}
                        title={file.name}
                      >
                        {file.name}
                      </a>
                    </div>
                  ))}
                </div>
              )}
              {/* Licencias (LA) */}
              {selectedLicenses.length > 0 && (
                <Button
                  type="button"
                  color={validateLicenses ? "success" : "primary"}
                  disabled={validateLicenses || loadingLA}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    validarPDFUploaded("LA");
                  }}
                  className="mt-3 flex items-center gap-2"
                >
                  {loadingLA && <BiLoaderAlt className="animate-spin" />}
                  {validateLicenses && !loadingLA && (
                    <BiCheckCircle className="text-green-500" />
                  )}
                  {loadingLA
                    ? "Cargando…"
                    : validateLicenses
                    ? "Validado"
                    : "Verificar"}
                </Button>
              )}
            </div>
          </FormGroup>
        </Col>
      </Row>
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {/* <button
          type="button"
          className="btn btn-secondary me-2"
          onClick={onBack}
        >
          Regresar
        </button> */}
        <button
          type="submit"
          className="btn btn-primary"
          onClick={handleOnSubmit}
        >
          Siguiente
        </button>
      </div>
      {/* </Form> */}
    </React.Fragment>
  );
};

export default LegalDocumentation;
